// Shared defaults for a NEW quote's sell rates and markups (decision 2,
// 2026-09-02). There is no stored shop-rate table: the per-quote Quote Setup
// rates are the pricing lever, and every place that seeds a new quote — the
// Quote page's "New" button and the nester's Create Quote modal — starts
// from THIS object so the two can never drift apart.
//
// Units are what QuoteManagementPage's `quoteSetup` state holds: $/hr for
// the six sell rates and PERCENT for the markups / margin (20 = 20%). The
// API takes FRACTIONS — convert at the boundary with quoteRatesToPayload /
// quoteHeaderToPayload, never here. `coating_markup: ''` means "follows
// material_markup" and is sent as null.
//
// No React, no fetch; quotePricing.js imports from here (never the reverse).

import { num, fmtRate } from './priceMath';

export const QUOTE_RATE_DEFAULTS = Object.freeze({
  laser_rate: 175,
  tap_rate: 130,
  brake_rate: 130,
  fabrication_rate: 130,
  blast_rate: 100,
  finish_rate: 100,
  material_markup: 20,
  coating_markup: '',
  target_margin: 40,
});

/** The six sell-rate fields in display order, with the short label the UI shows. */
export const QUOTE_SELL_RATE_FIELDS = Object.freeze([
  Object.freeze(['laser_rate', 'Laser']),
  Object.freeze(['tap_rate', 'Tap']),
  Object.freeze(['brake_rate', 'Brake']),
  Object.freeze(['fabrication_rate', 'Fab']),
  Object.freeze(['blast_rate', 'Blast']),
  Object.freeze(['finish_rate', 'Finish']),
]);

/** A fresh, mutable copy for component state; `overrides` win over the defaults. */
export function newQuoteRateSetup(overrides) {
  return { ...QUOTE_RATE_DEFAULTS, ...(overrides || {}) };
}

/** Percent (20, '20') → fraction (0.2); blank / null / non-numeric → null. */
export function pctToFraction(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = num(v, NaN);
  return Number.isFinite(n) ? n / 100 : null;
}

/**
 * The rate keys a calculator call (POST /quotes/calculate-part-cost) takes:
 * the six sell rates as $/hr numbers (blank / junk → 0 — the Quote page's
 * $0-rate banner is the guard), material_markup as a fraction (blank → 0)
 * and coating_markup as a fraction or null (= follow material_markup).
 */
export function quoteRatesToPayload(setup) {
  const s = setup || {};
  const out = {};
  QUOTE_SELL_RATE_FIELDS.forEach(([key]) => { out[key] = num(s[key], 0); });
  const mm = pctToFraction(s.material_markup);
  out.material_markup = mm == null ? 0 : mm;
  out.coating_markup = pctToFraction(s.coating_markup);
  return out;
}

/** quoteRatesToPayload + target_margin (fraction or null): the quote HEADER keys for POST / PATCH /quotes. */
export function quoteHeaderToPayload(setup) {
  const s = setup || {};
  return { ...quoteRatesToPayload(s), target_margin: pctToFraction(s.target_margin) };
}

/** "Laser $175.00/hr · Tap $130.00/hr · … · material markup 20%" — for notes and placeholders. Defaults when no setup is given. */
export function describeQuoteRates(setup) {
  const s = setup || QUOTE_RATE_DEFAULTS;
  const bits = QUOTE_SELL_RATE_FIELDS.map(([key, label]) => `${label} ${fmtRate(num(s[key], 0), { dash: false })}`);
  bits.push(`material markup ${num(s.material_markup, 0)}%`);
  return bits.join(' · ');
}
